import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import AnalyticsLogger from 'models/AnalyticsLogger';
import FeaturesUtil from 'utils/FeaturesUtil';
import AppCache from './AppCache';
import {
  type IExtras,
  type IHeaders,
  type IParams,
  type IRequestor,
  type IResponse,
} from './IRequestor';

type RequestMethod = NonNullable<AxiosRequestConfig['method']>;

export type IRequestConfig = {
  method: RequestMethod;
  url: string;
  expires?: number;
  isCacheable?: boolean;
  responseURL?: string;
} & IExtras;

export abstract class AbstractRequestor implements IRequestor {
  private readonly instance: AxiosInstance;
  private readonly baseURL: string | undefined;

  constructor(config: AxiosRequestConfig) {
    this.instance = axios.create(config);
    this.baseURL = config.baseURL;
  }

  head<T>({ url, params }: { url: string; params?: IParams }): Promise<T> {
    const config: IRequestConfig = { method: 'HEAD', url, params };
    return this._wrapper_<T>({ config });
  }

  get<T>({
    url,
    params,
    expires,
    headers,
    signal,
  }: {
    url: string;
    params?: IParams;
    expires?: number;
    headers?: IHeaders;
    signal?: AbortSignal;
  }): Promise<T> {
    const config: IRequestConfig = {
      method: 'GET',
      url,
      params,
      headers,
      expires,
      signal,
      isCacheable: true,
    };
    return this._wrapper_<T>({ config });
  }

  post<T>({
    url,
    data,
    headers,
    params,
    expires,
  }: {
    url: string;
    data?: unknown;
    headers?: IHeaders;
    params?: IParams;
    expires?: number;
  }): Promise<T> {
    const config: IRequestConfig = { method: 'POST', url, data, headers, params, expires };
    return this._wrapper_<T>({ config });
  }

  patch<T>({
    url,
    data,
    headers,
  }: {
    url: string;
    data?: unknown;
    headers?: IHeaders;
  }): Promise<T> {
    const config: IRequestConfig = { method: 'PATCH', url, data, headers };
    return this._wrapper_<T>({ config });
  }

  put<T>({ url, data, headers }: { url: string; data?: unknown; headers?: IHeaders }): Promise<T> {
    const config: IRequestConfig = { method: 'PUT', url, data, headers };
    return this._wrapper_<T>({ config });
  }

  delete<T>({
    url,
    data,
    headers,
  }: {
    url: string;
    data?: unknown;
    headers?: IHeaders;
  }): Promise<T> {
    const config: IRequestConfig = { method: 'DELETE', url, data, headers };
    return this._wrapper_<T>({ config });
  }

  request<T>({
    method,
    url,
    options,
    expires = 0,
    isCacheable = false,
  }: {
    method: RequestMethod;
    url: string;
    options: IExtras;
    expires?: number;
    isCacheable?: boolean;
  }): Promise<IResponse<T>> {
    const { headers, params, data, timeout, withCredentials } = options;
    const config: IRequestConfig = {
      method,
      url,
      expires,
      headers: { ...headers, ...this.instance.defaults.headers },
      params,
      data,
      timeout,
      withCredentials,
      isCacheable,
    };

    return this._wrapper_<IResponse<T>>({ config, returnFullResponse: true });
  }

  protected async _wrapper_<T>({
    config,
    returnFullResponse,
  }: {
    config: IRequestConfig;
    returnFullResponse?: boolean;
  }): Promise<T> {
    // Skip cache process, for certain requests
    if (
      AppCache.hasCache() ||
      !config.isCacheable ||
      config.expires === 0 ||
      config.method !== 'GET'
    ) {
      const res: any = await this._request_<T>(config);
      return returnFullResponse ? res : res.data;
    }

    // In other cases, make the request through the cache
    const request = config.url; // new Request(config.url, config)
    const params: IParams = {
      expires: 0,
      ...(config.params || config.data),
    };
    const cached: Response | any = await AppCache.get({ request, params });
    // Check if the request is already cached
    const { data, isCached }: { data: any; isCached: boolean } = await AppCache.isCached({
      cached,
    });
    // If possible, use cached version
    if (isCached) {
      return data;
    }
    // Otherwise, make a fresh request...
    const res: any = await this._request_<T>(config);
    const preparedData = {
      expires: config.expires || 0,
      data: returnFullResponse ? res : res.data,
      params,
    };
    const blob = new Blob([JSON.stringify(preparedData)], { type: 'application/json' });
    // ...then cache it
    const response = new Response(blob, res);
    AppCache.put({ params, request, response });
    return returnFullResponse ? res : res.data;
  }

  protected _request_<T>(config: IRequestConfig) {
    const { data, method, url } = config;

    if (FeaturesUtil.logAPIRequestToMixpanel()) {
      AnalyticsLogger.sendAPIRequest({
        URL: `${this.baseURL}${url}`,
        method,
        source: data?.source,
      });
    }

    return this.instance.request<T>(config);
  }
}
